import { Component, HostListener, OnInit } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { HxAuthService, HxStoreService, HxUserUiSettingsService, OrderAction, PermissionKey } from 'hx-services';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import Keycloak from 'keycloak-js';

const MN_SIDEBAR_HIDE_HINT_SEEN = 'mn_sidebar_hide_hint_seen';

interface MenuItem {
  id: string;
  title: string;
  isSub?: boolean;
  sub?: string;
  icon: string;
  link?: string;
  queryParams?: Record<string, unknown>;
  visible: boolean;
}

interface AnnotatedMenuItem {
  item: MenuItem;
  sectionTitle: string | null;
}

interface AnnotatedHiddenItem {
  item: MenuItem;
  sectionTitle: string | null;
}

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.css'],
    imports: [NgFor, NgIf, RouterLinkActive, RouterLink, NgClass, TranslocoPipe]
})
export class SidebarComponent implements OnInit {
  profileUrl = `${location.protocol}//profile.${location.hostname.split('.').slice(1).join('.')}`;

  mainmenu: MenuItem[] = [
    {
      id: 'section-stats',
      title: 'stats',
      isSub: true,
      icon: 'ellipsis-h',
      visible: false,
    },
    {
      id: 'statistics-sales',
      title: 'sales',
      sub: 'statistics',
      icon: 'shopping-cart',
      visible: false,
    },
    {
      id: 'statistics-city',
      title: 'city',
      sub: 'statistics',
      icon: 'city',
      visible: false,
    },
    {
      id: 'statistics-store',
      title: 'store',
      sub: 'statistics',
      icon: 'store-alt',
      visible: false,
    },
    {
      id: 'statistics-client',
      title: 'client',
      sub: 'statistics',
      icon: 'walking',
      visible: false,
    },
    {
      id: 'statistics-employees',
      title: 'employees',
      sub: 'statistics',
      icon: 'user-friends',
      visible: false,
    },
    {
      id: 'statistics-product-categories',
      title: 'product-categories',
      sub: 'statistics',
      icon: 'tags',
      visible: false,
    },
    {
      id: 'statistics-products',
      title: 'products',
      sub: 'statistics',
      icon: 'boxes',
      visible: false,
    },
    {
      id: 'statistics-abc-analysis',
      title: 'abc-analysis',
      sub: 'statistics',
      icon: 'stream',
      visible: false,
    },
    {
      id: 'statistics-rfm-analysis',
      title: 'rfm-analysis',
      sub: 'statistics',
      icon: 'project-diagram',
      visible: false,
    },
    {
      id: 'section-callcenter',
      title: 'call-center',
      isSub: true,
      icon: 'ellipsis-h',
      visible: false,
    },
    {
      id: 'order-stats',
      title: 'order-stats',
      icon: 'line-chart',
      visible: false,
    },
    {
      id: 'orders',
      title: 'orders',
      icon: 'list-ul',
      link: 'orders/list',
      queryParams: {activeCart: false},
      visible: true,
    },
    {
      id: 'orders-archive',
      title: 'orders-archive',
      icon: 'archive',
      link: 'orders/archive',
      queryParams: {activeCart: false, actions: OrderAction.RECEIVED},
      visible: false,
    },
    {
      id: 'feedback',
      title: 'feedback',
      link: 'orders/feedbacks',
      icon: 'comments-o',
      visible: false,
    },
    {
      id: 'rejections',
      title: 'rejections',
      link: 'rejections',
      icon: 'sign-out-alt',
      visible: false,
    },
    {
      id: 'section-control',
      title: 'control',
      isSub: true,
      icon: 'ellipsis-h',
      visible: false,
    },
    {
      id: 'store-info',
      title: 'store-info',
      icon: 'store',
      visible: false,
    },
    {
      id: 'control-invoices',
      title: 'invoices',
      icon: 'file-text',
      visible: false,
    },
    {
      id: 'clients',
      title: 'clients',
      link: 'clients',
      icon: 'users',
      visible: false,
    },
    {
      id: 'limits',
      title: 'limits',
      icon: 'hand-paper-o',
      visible: false,
    },
    {
      id: 'users',
      title: 'users',
      icon: 'user',
      visible: false,
    },
    {
      id: 'delivery',
      title: 'delivery',
      icon: 'truck',
      visible: false,
    },
    {
      id: 'timesheet',
      title: 'timesheet',
      icon: 'clock',
      visible: false,
    },
    {
      id: 'settings',
      title: 'settings',
      icon: 'cog',
      visible: false,
    },
    {
      id: 'faq',
      title: 'faq',
      link: 'faq/list',
      icon: 'bell',
      visible: false,
    },
    {
      id: 'royalty',
      title: 'royalty',
      icon: 'wallet',
      visible: false,
    },
    {
      id: 'section-reports',
      title: 'reports.title',
      isSub: true,
      icon: 'ellipsis-h',
      visible: false,
    },
    {
      id: 'reports-money',
      title: 'reports.money',
      link: 'reports/money',
      icon: 'money',
      visible: false,
    },
    {
      id: 'reports-money-flow',
      title: 'reports.money-flow',
      link: 'reports/money-flow',
      icon: 'money-check',
      visible: false,
    },
    {
      id: 'reports-income',
      title: 'reports.income',
      link: 'reports',
      icon: 'file-invoice-dollar',
      visible: false,
    },
    {
      id: 'section-storages',
      title: 'storages',
      isSub: true,
      icon: 'ellipsis-h',
      visible: false,
    },
    {
      id: 'storage-banks',
      title: 'banks',
      sub: 'storage',
      icon: 'dollar',
      visible: false,
    },
    {
      id: 'storage-products',
      title: 'products',
      sub: 'storage',
      icon: 'birthday-cake',
      visible: false,
    },
    {
      id: 'storage-suppliers',
      title: 'suppliers',
      sub: 'storage',
      icon: 'rocket',
      visible: false,
    },
    {
      id: 'storage-contracts',
      title: 'contracts',
      sub: 'storage',
      icon: 'file-contract',
      visible: false,
    },
    {
      id: 'storage-invoices',
      title: 'invoices',
      sub: 'storage',
      icon: 'shopping-cart',
      visible: false,
    },
    {
      id: 'storage-payment-approvals',
      title: 'payment-approvals',
      sub: 'storage',
      icon: 'book',
      visible: false,
    },
    {
      id: 'storage-spendings',
      title: 'spendings',
      sub: 'storage',
      icon: 'file-text',
      visible: false,
    },
    {
      id: 'section-confectionary',
      title: 'confectionary',
      isSub: true,
      icon: 'ellipsis-h',
      visible: false,
    },
    {
      id: 'cf-work-history',
      title: 'work-history',
      sub: 'cf',
      icon: 'history',
      visible: false,
    },
  ];

  menu = {
    settings: {
      click: true
    }
  };

  /** Set of tab ids the user has explicitly hidden. */
  hiddenTabIds = new Set<string>();

  /** Whether the "Скрытые" accordion is expanded. */
  hiddenGroupExpanded = false;

  /** Whether a PUT is in-flight (prevents overlapping saves). */
  savingInProgress = false;

  /** Precomputed list of visible items with section header info. */
  annotatedVisibleItems: AnnotatedMenuItem[] = [];

  /** Precomputed list of hidden items with section header info. */
  annotatedHiddenItems: AnnotatedHiddenItem[] = [];

  /** Count of hidden items (used in template without calling a method). */
  hiddenCount = 0;

  /** Whether the first-time discoverability hint for tab hiding is visible. */
  showHideHint = false;

  @HostListener('document:click', ['$event'])
  handleKeyboardEvent(event: MouseEvent) {
    const body = document.getElementsByTagName('body')[0];
    const hasClass = body.className.indexOf('--on');

    if (hasClass !== -1 && (event.target as HTMLElement).className.indexOf('open-sidebar') === -1) {
      this.toggleMobSidebar();
    }
  }

  constructor(
    private keycloak: Keycloak,
    private router: Router,
    private storeService: HxStoreService,
    private authService: HxAuthService,
    private userUiSettingsService: HxUserUiSettingsService,
  ) {
  }

  async ngOnInit() {
    // Load hidden tabs from backend first (reconciled against live menu)
    try {
      const settings = await this.userUiSettingsService.getHiddenTabs('manager');
      const liveIds = new Set(this.mainmenu.map(m => m.id));
      this.hiddenTabIds = new Set(settings.hiddenTabs.filter(id => liveIds.has(id)));
    } catch {
      // Non-fatal: proceed without persisted hidden tabs
      this.hiddenTabIds = new Set();
    }

    this.storeService.getAvailableForPaymentApprovals().subscribe(result => {
      if (result.length) {
        this.mainmenu.forEach(r => {
          if (r.id === 'storage-payment-approvals') {
            r.visible = true;
          }
        });
        this.rebuildAnnotated();
      }
    });

    this.authService.hasPermission(PermissionKey.auditor).then(r => {
      if (r.hasPermission) {
        this.mainmenu.forEach(item => {
          if (item.id === 'royalty') {
            item.visible = true;
          }
        });
        this.rebuildAnnotated();
      }
    });

    const r = await this.storeService.getAllStoresByCurrentUserAndComponentType();
    if (r.length > 1) {
      this.mainmenu.forEach(item => {
        if (item.id === 'statistics-store') {
          item.visible = true;
        }
      });
    }
    const uniqueIds = [...new Set(r.map(t => t.cityId))];
    if (uniqueIds.length > 1) {
      this.mainmenu.forEach(item => {
        if (item.id === 'statistics-city') {
          item.visible = true;
        }
      });
    }

    const res = await this.authService.hasPermission(PermissionKey.cb_order_create_in_mn);
    if (!res.hasPermission) {
      this.mainmenu.forEach(item => {
        if (!['royalty', 'statistics-store', 'storage-payment-approvals', 'statistics-city'].includes(item.id)) {
          item.visible = true;
        }
      });
    } else {
      this.mainmenu.forEach(item => {
        if (['statistics-store', 'statistics-city'].includes(item.id)) {
          item.visible = false;
        }
      });
    }

    this.rebuildAnnotated();

    // Show one-time discoverability hint if the user has never seen it,
    // has at least one visible item, and has not hidden anything yet.
    try {
      if (
        localStorage.getItem(MN_SIDEBAR_HIDE_HINT_SEEN) === null &&
        this.annotatedVisibleItems.length > 0 &&
        this.hiddenTabIds.size === 0
      ) {
        this.showHideHint = true;
      }
    } catch {
      // Safari private mode or disabled storage — treat as "don't show"
    }
  }

  private rebuildAnnotated() {
    const liveNonSectionVisible = this.mainmenu.filter(item => !item.isSub && item.visible);
    const visibleItems = liveNonSectionVisible.filter(item => !this.hiddenTabIds.has(item.id));
    const hiddenItems = liveNonSectionVisible.filter(item => this.hiddenTabIds.has(item.id));

    this.annotatedVisibleItems = visibleItems.map(item => ({
      item,
      sectionTitle: this.computeSectionTitle(item, visibleItems),
    }));

    this.annotatedHiddenItems = hiddenItems.map(item => ({
      item,
      sectionTitle: this.computeSectionTitle(item, hiddenItems),
    }));

    this.hiddenCount = hiddenItems.length;
  }

  private computeSectionTitle(item: MenuItem, inItems: MenuItem[]): string | null {
    const idx = this.mainmenu.indexOf(item);
    for (let i = idx - 1; i >= 0; i--) {
      if (this.mainmenu[i].isSub) {
        const header = this.mainmenu[i];
        if (!header.visible) {
          return null;
        }
        // Only show the header before the first item in this group within inItems
        const firstInGroup = inItems.find(m => this.mainmenu.indexOf(m) > i);
        return firstInGroup === item ? header.title : null;
      }
    }
    return null;
  }

  toggleMobSidebar(): void {
    const body = document.getElementsByTagName('body')[0];
    const hasClass = body.className.indexOf('--on');

    if (hasClass !== -1) {
      body.classList.remove('m-aside-left--on');
    } else {
      body.classList.add('m-aside-left--on');
    }
  }

  logout() {
    this.keycloak.logout();
  }

  routerLink(item: MenuItem): string {
    if (item.link) {
      return `/${item.link}`;
    }
    if (item.sub) {
      return `/${item.sub}/${item.title}`;
    }
    return `/${item.title}`;
  }

  dismissHideHint() {
    this.showHideHint = false;
    try {
      localStorage.setItem(MN_SIDEBAR_HIDE_HINT_SEEN, '1');
    } catch {
      // Silently ignore storage errors
    }
  }

  async hideTab(tabId: string) {
    this.dismissHideHint();
    if (this.savingInProgress) {
      return;
    }
    this.savingInProgress = true;
    const newHidden = new Set(this.hiddenTabIds);
    newHidden.add(tabId);
    try {
      const result = await this.userUiSettingsService.saveHiddenTabs({
        app: 'manager',
        hiddenTabs: [...newHidden],
      });
      const liveIds = new Set(this.mainmenu.map(m => m.id));
      this.hiddenTabIds = new Set(result.hiddenTabs.filter(id => liveIds.has(id)));
    } catch {
      // Non-fatal: leave state unchanged on error
    } finally {
      this.savingInProgress = false;
      this.rebuildAnnotated();
    }
  }

  async unhideTab(tabId: string) {
    if (this.savingInProgress) {
      return;
    }
    this.savingInProgress = true;
    const newHidden = new Set(this.hiddenTabIds);
    newHidden.delete(tabId);
    try {
      const result = await this.userUiSettingsService.saveHiddenTabs({
        app: 'manager',
        hiddenTabs: [...newHidden],
      });
      const liveIds = new Set(this.mainmenu.map(m => m.id));
      this.hiddenTabIds = new Set(result.hiddenTabs.filter(id => liveIds.has(id)));
      if (this.hiddenTabIds.size === 0) {
        this.hiddenGroupExpanded = false;
      }
    } catch {
      // Non-fatal
    } finally {
      this.savingInProgress = false;
      this.rebuildAnnotated();
    }
  }

  toggleHiddenGroup() {
    this.hiddenGroupExpanded = !this.hiddenGroupExpanded;
  }
}
